import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getMenuList, deleteMenu, addMenu, updateMenu } from '../../services/menuService';
import MenuForm from './MenuForm';
import './Menu.scss';

const MenuList = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const response = await getMenuList();
      if (response) {
        setMenus(response || []);
      } else {
        message.error('Menüler yüklenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
      message.error('Menüler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (menu) => {
    const canEdit = menu.isPage && menu.dynamicPage;
    
    if (!canEdit) {
      message.warning('Bu menü düzenlenemez. Düzenlenebilmesi için hem "Sayfa Menüsü" hem de "Dinamik İçerikli Sayfa" olmalıdır.');
      return;
    }
    setSelectedMenu(menu);
    setShowForm(true);
  };

  const handleAdd = () => {
    setSelectedMenu(null);
    setShowForm(true);
  };

  const handleDelete = async (guid) => {
    try {
      const response = await deleteMenu(guid);
      if (response.isSuccess) {
        message.success('Menü başarıyla silindi');
        fetchMenus();
      } else {
        message.error(response.resultMessage || 'Menü silinirken bir hata oluştu');
      }
    } catch (error) {
      message.error('Menü silinirken bir hata oluştu');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      let response;
      if (selectedMenu) {
        response = await updateMenu({ ...formData, guid: selectedMenu.guid });
      } else {
        response = await addMenu(formData);
      }

      if (response.isSuccess) {
        message.success(`Menü başarıyla ${selectedMenu ? 'güncellendi' : 'eklendi'}`);
        setShowForm(false);
        fetchMenus();
      } else {
        message.error(response.resultMessage || `Menü ${selectedMenu ? 'güncellenirken' : 'eklenirken'} bir hata oluştu`);
      }
    } catch (error) {
      message.error(`Menü ${selectedMenu ? 'güncellenirken' : 'eklenirken'} bir hata oluştu`);
    }
  };

  const columns = [
    {
      title: 'Sıra',
      dataIndex: 'displayOrder',
      width: 80,
      sorter: (a, b) => a.displayOrder - b.displayOrder,
    },
    {
      title: 'Menü Adı',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.code && <Tag color="blue">{record.code}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Özellikler',
      key: 'features',
      render: (_, record) => (
        <Space>
          {record.isPage && <Tag color="processing">Sayfa</Tag>}
          {record.dynamicPage && <Tag color="success">Dinamik İçerik</Tag>}
        </Space>
      ),
    },
    {
      title: 'Alt Menüler',
      dataIndex: 'subMenus',
      render: (subMenus) => subMenus?.length > 0 ? <Tag color="warning">{subMenus.length} alt menü</Tag> : '-',
    },
    {
      title: 'İşlemler',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={!(record.isPage && record.dynamicPage)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu menüyü silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.guid)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    if (!record.subMenus?.length) return null;

    return (
      <Table
        columns={columns}
        dataSource={record.subMenus}
        pagination={false}
        rowKey="guid"
      />
    );
  };

  return (
    <div className="menu-page">
      <div className="arc-page-header">
        <h2>Menü Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Menü Ekle
        </Button>
      </div>
      <Card>
      <Table
        columns={columns}
        dataSource={menus}
        rowKey="guid"
        expandable={{
          expandedRowRender,
          rowExpandable: record => record.subMenus?.length > 0,
        }}
        loading={loading}
        pagination={{
          total: menus.length,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} menü`,
        }}
      />

      <MenuForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSubmit={handleSubmit}
        menuItem={selectedMenu}
      />
    </Card>
    </div>
  );
};

export default MenuList;
